import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Bell, Save, Loader2, Eye, EyeOff } from "lucide-react";
import ImageTextarea, { ContentWithImages } from "@/components/ImageTextarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RegistrationNoticeManagement({ user }) {
  const [notice, setNotice] = useState({ title: "", content: "", is_active: false });
  const [originalNotice, setOriginalNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchNotice();
  }, []);

  const fetchNotice = async () => {
    try {
      const response = await axios.get(`${API}/academy/registration-notice`);
      const data = response.data || { title: "", content: "", is_active: false };
      setNotice(data);
      setOriginalNotice(data);
    } catch (error) {
      console.error("Error fetching notice:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/academy/admin/registration-notice`,
        {
          title: notice.title,
          content: notice.content,
          is_active: notice.is_active,
        },
        { withCredentials: true }
      );
      toast.success("Notice saved successfully!");
      setOriginalNotice({ ...notice });
    } catch (error) {
      console.error("Error saving notice:", error);
      toast.error("Failed to save notice");
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    if (!originalNotice) return false;
    return (
      notice.title !== originalNotice.title ||
      notice.content !== originalNotice.content ||
      notice.is_active !== originalNotice.is_active
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <header className="bg-slate-950 border-b border-white/5 shadow-lg">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/academy">
              <Button variant="ghost" className="text-white hover:text-blue-200">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Academy
              </Button>
            </Link>
            <h1 className="text-2xl font-bold text-white">📢 Registration Notice</h1>
          </div>
          <div className="flex items-center gap-3">
            {notice.is_active ? (
              <span className="bg-emerald-500/100 text-white px-3 py-1 rounded-full text-sm font-medium">
                ✓ Notice Active
              </span>
            ) : (
              <span className="bg-slate-700 text-slate-300 px-3 py-1 rounded-full text-sm font-medium">
                Notice Hidden
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Card className="p-6 mb-6 bg-blue-500/10 border-blue-500/30">
          <div className="flex items-start gap-3">
            <Bell className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
            <div>
              <h2 className="font-semibold text-blue-300">Parent Registration Notice</h2>
              <p className="text-sm text-blue-400 mt-1">
                This notice appears at the start of the registration process. Use it to inform parents about fee changes, important updates, or any other information they should know before registering.
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          {/* Toggle */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b">
            <div className="flex items-center gap-3">
              {notice.is_active ? (
                <Eye className="w-5 h-5 text-emerald-400" />
              ) : (
                <EyeOff className="w-5 h-5 text-slate-400" />
              )}
              <div>
                <p className="font-medium">Show Notice to Parents</p>
                <p className="text-sm text-slate-400">
                  {notice.is_active
                    ? "Notice is visible before registration starts"
                    : "Notice is hidden - parents go directly to registration"}
                </p>
              </div>
            </div>
            <Switch
              checked={notice.is_active}
              onCheckedChange={(checked) => setNotice({ ...notice, is_active: checked })}
            />
          </div>

          {/* Title */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Notice Title
            </label>
            <Input
              value={notice.title}
              onChange={(e) => setNotice({ ...notice, title: e.target.value })}
              placeholder="Important Notice for Parents"
            />
          </div>

          {/* Content */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Notice Content
            </label>
            <ImageTextarea
              value={notice.content}
              onChange={(value) => setNotice({ ...notice, content: value })}
              placeholder="Enter your message to parents here... You can insert images at any position."
              rows={15}
              className="font-mono text-sm"
            />
            <p className="text-xs text-slate-400 mt-1">
              Line breaks will be preserved. Use bullet points (•) for lists. Click "Insert Image" to add images inline.
            </p>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={!hasChanges() || saving}
              className={hasChanges() ? "bg-green-600 hover:bg-green-700" : ""}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {hasChanges() ? "Save Changes" : "No Changes"}
            </Button>
          </div>
        </Card>

        {/* Preview */}
        {notice.is_active && (
          <Card className="p-6 mt-6 bg-amber-500/10 border-amber-500/30">
            <h3 className="font-semibold mb-4 text-amber-400">📱 Preview (as seen by parents)</h3>
            <div className="bg-slate-900/50 rounded-lg border border-amber-500/30 p-6 max-w-2xl mx-auto">
              <div className="text-center mb-4">
                <Bell className="w-12 h-12 text-amber-500 mx-auto mb-2" />
                <h2 className="text-xl font-bold text-white">{notice.title || "Notice Title"}</h2>
              </div>
              <ContentWithImages 
                content={notice.content || "Notice content will appear here..."} 
                className="text-sm text-slate-200"
              />
              <div className="mt-6 text-center">
                <Button className="bg-blue-600">
                  Continue to Registration →
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
